import { useQuery, useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ShowToast } from "../../components/toast";
import { PRICING_STREAM_QUERY_KEY } from "./use-pricing-stream";

const QUERY_KEYS = {
  config: "multiplierConfig",
};

const CALENDAR_QUERY_KEYS = ["calendarMetrics", "calendarData", "dayMetrics"];

export function invalidatePricingAndCalendar(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.config] });
  queryClient.invalidateQueries({ queryKey: [PRICING_STREAM_QUERY_KEY] });
  CALENDAR_QUERY_KEYS.forEach((key) =>
    queryClient.invalidateQueries({ queryKey: [key] }),
  );
}

export function useMultiplierConfig(userId?: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.config, userId],
    queryFn: () => api.multiplierConfig.get(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });
}

export function usePricingActivity(userId?: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.config, userId],
    queryFn: () => api.multiplierConfig.get(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
    select: (data: Record<string, unknown>) => {
      const changes = (data?.changeHistory as Array<Record<string, unknown>>) ?? [];
      return changes.filter(
        (entry: Record<string, unknown>) => entry.type === "config_change"
      );
    },
  });
}

export function useUndoConfigChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, changeId }: { userId: string; changeId: string }) =>
      api.multiplierConfig.undo(userId, changeId),
    onSuccess: () => {
      ShowToast({ message: "Change reverted successfully", type: "success" });
      invalidatePricingAndCalendar(queryClient);
    },
    onError: () => {
      ShowToast({ message: "Failed to revert change", type: "error" });
    },
  });
}

export function useResetConfigToDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      resetBase,
      deleteRules,
    }: {
      userId: string;
      resetBase?: boolean;
      deleteRules?: boolean;
    }) => api.multiplierConfig.reset(userId, { resetBase, deleteRules }),
    onSuccess: () => {
      ShowToast({ message: "Config reset to defaults", type: "success" });
      invalidatePricingAndCalendar(queryClient);
    },
    onError: () => {
      ShowToast({ message: "Failed to reset config", type: "error" });
    },
  });
}

export function useRoomTypes(userId?: string) {
  return useQuery<Array<{ label: string; value: string }>>({
    queryKey: ["roomTypes", userId],
    queryFn: async () => {
      const res = await api.getHotelData(userId!);
      return (res?.roomTypes || []).map((rt: { name: string; pmsId: string }) => ({
        label: rt.name,
        value: rt.pmsId,
      }));
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRemoveFloorCeilings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roomTypePmsId }: { userId: string; roomTypePmsId?: string }) =>
      roomTypePmsId
        ? api.removeFloorCeilingsByRoom(userId, roomTypePmsId)
        : api.removeAllFloorCeilings(userId),
    onMutate: async ({ userId, roomTypePmsId }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.config, userId] });
      const previous = queryClient.getQueryData([QUERY_KEYS.config, userId]);
      queryClient.setQueryData([QUERY_KEYS.config, userId], (old: Record<string, unknown> | undefined) => {
        if (!old?.floorCeilingOverrides) return old;
        const overrides = old.floorCeilingOverrides as Array<{ roomTypePmsId: string }>;
        return {
          ...old,
          floorCeilingOverrides: roomTypePmsId
            ? overrides.filter((o) => o.roomTypePmsId !== roomTypePmsId)
            : [],
        };
      });
      return { previous, userId };
    },
    onSuccess: (_, { roomTypePmsId }) => {
      ShowToast({
        message: roomTypePmsId ? "Override removed" : "All overrides removed",
        type: "success",
      });
      invalidatePricingAndCalendar(queryClient);
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && context?.userId) {
        queryClient.setQueryData([QUERY_KEYS.config, context.userId], context.previous);
      }
      ShowToast({ message: "Failed to remove overrides", type: "error" });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, ruleId }: { userId: string; ruleId: string }) =>
      api.multiplierConfig.deleteRule(userId, ruleId),
    onMutate: async ({ userId, ruleId }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.config, userId] });
      const previous = queryClient.getQueryData([QUERY_KEYS.config, userId]);
      queryClient.setQueryData([QUERY_KEYS.config, userId], (old: Record<string, unknown> | undefined) => {
        if (!old?.rules) return old;
        const rules = old.rules as Array<{ id: string }>;
        return { ...old, rules: rules.filter((r) => r.id !== ruleId) };
      });
      return { previous, userId };
    },
    onSuccess: () => {
      invalidatePricingAndCalendar(queryClient);
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && context?.userId) {
        queryClient.setQueryData([QUERY_KEYS.config, context.userId], context.previous);
      }
      ShowToast({ message: "Failed to delete rule", type: "error" });
    },
  });
}
